import { Add, Delete, Edit } from '@mui/icons-material';
import { IconButton, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import _ from 'lodash';
import React, { useContext, useMemo, useState } from 'react';
import AccordionSection from '../../AccordionSection';
import ResponsiveButton from '../../../uikit/buttons/ResponsiveButton';
import { DisableContext } from '../../../contexts/DisableContextProvider';
import { EmployeeRequirement } from '../../../types/EmployeeRequirement';
import { UserSettingsCollapseKeys } from '../../../types/User';
import AddEmployeeRequirementModal from './AddEmployeeRequirementModal';
import PreferencePill from '../../preferences/PreferencePill';

interface Props {
  collapseId: UserSettingsCollapseKeys;
  employeeRequirements: EmployeeRequirement[];
  setEmployeeRequirements: (reqs: EmployeeRequirement[]) => void;
}

export default function EmployeeRequirementSection({ collapseId, employeeRequirements, setEmployeeRequirements }: Props) {
  const { disabled } = useContext(DisableContext);

  const [isModalOpen, setModalOpen] = useState(false);
  const [editingRequirement, setEditingRequirement] = useState<EmployeeRequirement | null>(null);

  // Prevent duplicate roles
  const selectedEmployeeRoles = useMemo(() => employeeRequirements.map((r) => r.employee_role_uuid), [employeeRequirements]);

  const onAddOrUpdate = (req: EmployeeRequirement) => {
    const updated = employeeRequirements.some((r) => r.uuid === req.uuid)
      ? employeeRequirements.map((r) => (r.uuid === req.uuid ? req : r))
      : [...employeeRequirements, req];

    setEmployeeRequirements(updated);
    setEditingRequirement(null);
    setModalOpen(false);
  };

  const onEdit = (uuid: string) => {
    setEditingRequirement(employeeRequirements.find((r) => r.uuid === uuid) || null);
    setModalOpen(true);
  };

  const onDelete = (uuid: string) => {
    setEmployeeRequirements(employeeRequirements.filter((r) => r.uuid !== uuid));
  };

  return (
    <>
      <AccordionSection
        title="Employee Requirements"
        collapseId={collapseId}
        hasData={employeeRequirements.length > 0}
        headerElements={
          disabled ? null : (
            <ResponsiveButton startIcon={<Add />} variant="outlined" onClick={() => setModalOpen(true)}>
              Add
            </ResponsiveButton>
          )
        }
      >
        {employeeRequirements.length === 0 ? (
          <Typography color="text.secondary">No employee requirements added</Typography>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Employee Role</TableCell>
                <TableCell>Quantity</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {_.map(employeeRequirements, (req) => (
                <TableRow key={req.uuid || crypto.randomUUID()}>
                  <TableCell>
                    <PreferencePill value={req.employee_role_value} color={req.employee_role_color ?? ''} />
                  </TableCell>
                  <TableCell>{req.quantity}</TableCell>
                  <TableCell>
                    <IconButton onClick={() => onEdit(req.uuid)} disabled={disabled}>
                      <Edit />
                    </IconButton>
                    <IconButton onClick={() => onDelete(req.uuid)} disabled={disabled}>
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </AccordionSection>

      <AddEmployeeRequirementModal
        isOpen={isModalOpen}
        requirement={editingRequirement}
        selectedEmployeeRoles={selectedEmployeeRoles}
        onCancel={() => {
          setModalOpen(false);
          setEditingRequirement(null);
        }}
        onSubmit={onAddOrUpdate}
      />
    </>
  );
}
